import MessageService from 'message-service/message-service';

import TrackingEvents from './lib/enums/events';
import gameStatePropHelper from './lib/utils/gameStatePropHelper';

const {
    GD5_APP_PAGELOAD_TRACKING,
    GD5_APP_ASYNC_TRACKING
} = TrackingEvents;

const pageNamesMap = {
    Preview: 'Preview',
    Live: 'Live',
    Final: 'Final'
};

const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

class GamedayTrackingEngine {
    constructor (opts) {
        this.config = opts;
        this.pageNamePrefix = 'Major League Baseball:';
        this.propertyMap = this.propertyMap || {
            pageName: 'prop13',
            dynamic_component: 'prop12',
            dynamic_componentAction: 'prop14',
            gameday_matchup: 'prop40',
            site_affiliateId: 'eVar4',
            selectedIndex: 'eVar84',
            componentText: 'eVar140'
        };
        this.lastGame = '';
        this.listen();
    }

    init () {
        this.initialized = true;
    }

    createTrackingObject (props = {}) {
        const map = this.propertyMap || {};
        let prop;
        let mapped;
        const { shouldRewritePageName = true } = props;

        const trackingObject = {
            shouldRewritePageName
        };

        for (prop in props) {
            mapped = map[prop] || prop;
            trackingObject[mapped] = props[prop];
        }

        if (props.pageName && shouldRewritePageName) {
            trackingObject.pageName = props.pageName;
        }

        if (props.channel) {
            trackingObject.channel = props.channel;
        }

        if (props.events) {
            trackingObject.events = props.events;
        }

        if (props.linkName) {
            trackingObject.linkName = props.linkName;
        }

        return trackingObject;
    }

    /**
         * Async tracking function to be implemented by the section component.
         */
    trackPageView () {
    }

    trackAsync () {
        console.warn('gd tracking engine', 'trackAsync not implemented');
    }

    listen () {
        MessageService.off.call(this, GD5_APP_ASYNC_TRACKING, this.asyncHandler);
        MessageService.off.call(this, GD5_APP_PAGELOAD_TRACKING, this.pageHandler);
        // MessageService.off.call(this, GAME_SWITCH_COMPLETE, this.pageHandler);

        MessageService.on.call(this, GD5_APP_ASYNC_TRACKING, this.asyncHandler);
        MessageService.on.call(this, GD5_APP_PAGELOAD_TRACKING, this.pageHandler);
        // MessageService.on.call(this, GAME_SWITCH_COMPLETE, this.pageHandler);
    }

    // User event track
    asyncHandler (data) {
        const gameStateProp = gameStatePropHelper(data);
        const isAtBat = typeof data?.atBatClickplayIndex !== 'undefined' && data?.atBatClickplayIndex !== '';
        const { gamedayMini = false, linkName = '', shouldRewritePageName = true, dfvReplay } = data;

        const base = {
            componentName: data?.componentName,
            dynamic_component: data?.dynamic_component,
            dynamic_componentAction: data?.dynamic_componentAction
        };

        const trackingData = { ...this.config.props, ...base, ...gameStateProp };
        const pageNameComponentText = data?.componentName || '';
        const { gameData } = data;
        const viewType = gameData?.gameView?.replace(/^\w/, (c) => c.toUpperCase());

        if (pageNameComponentText && shouldRewritePageName) {
            trackingData.pageName = trackingData.pageName + ': ' + pageNameComponentText;
        }

        if (isAtBat) {
            trackingData.pageName = trackingData.pageName + ': ' + pageNamesMap[viewType];
            trackingData.prop13 = trackingData.pageName;
        }

        if (data?.componentText) {
            trackingData.eVar140 = data?.componentText;
        }

        if (data?.selectedIndex) {
            trackingData.eVar84 = data.selectedIndex;
        }

        if (data?.events) {
            trackingData.events = data.events;
        }

        if (linkName) { // track any linkName that is included in a click
            trackingData.linkName = linkName;
            trackingData.linkType = 'o';
        }

        if (gamedayMini) {
            if (trackingData.linkName) { // include linkName here if provided from trackingConfig in Gameday Mini.
                trackingData.linkName = trackingData.linkName.replace('@GAME_STATE@', pageNamesMap[viewType]);
            }
        }

        if (dfvReplay) {
            trackingData.eVar171 = gameStateProp?.prop44;
        }

        this.trackAsync(this.createTrackingObject(trackingData));
    }

    // Page Load Tracking
    pageHandler (config) {
        const { playIndex, fieldvision, dfv, gamedayMini } = config || {};
        const isAtBat = typeof playIndex !== 'undefined' && playIndex !== '';
        const isFieldVision = fieldvision;
        const gameData = config?.gameData;
        const props = this.config.props;
        const gameStateProp = gameStatePropHelper(config || {});
        const trackingData = { ...props, ...gameStateProp };
        const clientId = gameData?.game?.clientId;
        const viewType = gameData?.gameView?.replace(/^\w/, (c) => c.toUpperCase());
        const tabType = gameData?.gameTab;

        const finalProps = { ...this.createTrackingObject(trackingData) };
        finalProps.prop46 = window?.location?.href || '';

        if (finalProps.shouldRewritePageName) {
            finalProps.pageName = finalProps.pageName + ': ' + pageNamesMap[viewType] + (tabType && ` ${capitalizeFirstLetter(tabType)} Tab`);
        }

        if (clientId) {
            finalProps.prop22 = clientId;
        }

        if (isAtBat) {
            finalProps.pageName = finalProps.pageName + ': Play Detail';
        }

        if (isFieldVision) {
            finalProps.pageName = finalProps.pageName + ': ' + 'Gameday 3D Live';
        }

        if (dfv) {
            finalProps.pageName = finalProps.pageName + ': ' + 'Batter View';
        }

        if (gamedayMini) {
            finalProps.prop12 = finalProps.appName;
            finalProps.prop13 = finalProps.prop13 || props.pageName; // if prop13 is set explicitly, we should use it, else use pageName.
            if (props.prop14) {
                finalProps.prop14 = props.prop14.replace('@GAME_STATE@', pageNamesMap[viewType]);
            } else {
                finalProps.prop14 = finalProps.appName + ': ' + 'Page Load';
            }
            if (props.linkName) {
                finalProps.linkName = props.linkName.replace('@GAME_STATE@', pageNamesMap[viewType]);
                finalProps.linkType = 'o'; // have to include linkType for it to be tracked by Adobe.
            }
            finalProps.shouldClearVars = false;
        } else {
            delete finalProps.prop13;
        }

        this.trackPageView(finalProps);
    }
};

export default GamedayTrackingEngine;
